import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type { Tenant } from "../types";

interface Props {
  onOpenDetail: (id: number) => void;
}

const LIMIT = 50;

const integrationModeColor = (mode: string): "info" | "success" | "default" => {
  if (mode === "shared") return "info";
  if (mode === "invite_only") return "success";
  return "default";
};

const integrationModeLabel = (mode: string): string => {
  if (mode === "shared") return "Shared";
  if (mode === "invite_only") return "Invite Only";
  if (mode === "isolated") return "Isolated";
  return mode;
};

export default function Tenants({ onOpenDetail }: Props) {
  const authApi = useAuthApi();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createMode, setCreateMode] = useState("invite_only");
  const [createAdminEmail, setCreateAdminEmail] = useState("");
  const [createAdminName, setCreateAdminName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadTenants = () => {
    setLoading(true);
    setError(null);
    authApi<Tenant[]>(`${PLATFORM}/tenants?limit=${LIMIT}&offset=${offset}`)
      .then((data) => setTenants(data ?? []))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load tenants"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    authApi<Tenant[]>(`${PLATFORM}/tenants?limit=${LIMIT}&offset=${offset}`)
      .then((data) => {
        if (!cancelled) setTenants(data ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load tenants");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [authApi, offset]);

  const handleCreate = () => {
    setCreating(true);
    setCreateError(null);
    const body: Record<string, string> = {
      name: createName,
      slug: createSlug,
      integration_mode: createMode,
    };
    if (createAdminEmail.trim()) body.admin_email = createAdminEmail.trim();
    if (createAdminName.trim()) body.admin_name = createAdminName.trim();

    authApi<Tenant>(`${PLATFORM}/tenants`, {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(() => {
        setCreateOpen(false);
        setCreateName("");
        setCreateSlug("");
        setCreateMode("invite_only");
        setCreateAdminEmail("");
        setCreateAdminName("");
        loadTenants();
      })
      .catch((e: unknown) => setCreateError(e instanceof Error ? e.message : "Failed to create tenant"))
      .finally(() => setCreating(false));
  };

  return (
    <Box>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
              Create Tenant
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Integration Mode</TableCell>
                <TableCell>Stripe</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onOpenDetail(t.id)}
                >
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.slug}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.status}
                      color={t.status === "active" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={integrationModeLabel(t.integration_mode)}
                      color={integrationModeColor(t.integration_mode)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t.stripe_customer_id ?? "Not connected"}>
                      <Chip
                        label={t.stripe_customer_id ? "Connected" : "Not connected"}
                        color={t.stripe_customer_id ? "success" : "default"}
                        size="small"
                        variant={t.stripe_customer_id ? "filled" : "outlined"}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">No tenants found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              size="small"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={tenants.length < LIMIT}
              onClick={() => setOffset((o) => o + LIMIT)}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Tenant</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {createError && <Alert severity="error">{createError}</Alert>}
          <TextField
            label="Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={creating}
            fullWidth
          />
          <TextField
            label="Slug"
            value={createSlug}
            onChange={(e) => setCreateSlug(e.target.value)}
            disabled={creating}
            helperText="Unique identifier, e.g. acme-cpo"
            fullWidth
          />
          <Select
            value={createMode}
            onChange={(e) => setCreateMode(e.target.value)}
            disabled={creating}
            fullWidth
          >
            <MenuItem value="invite_only">Invite Only</MenuItem>
            <MenuItem value="shared">Shared</MenuItem>
            <MenuItem value="isolated">Isolated</MenuItem>
          </Select>
          <TextField
            label="Admin Email (optional)"
            value={createAdminEmail}
            onChange={(e) => setCreateAdminEmail(e.target.value)}
            disabled={creating}
            helperText="Invite email of the initial portal admin user. If no account exists yet, it will be linked on first login."
            fullWidth
          />
          <TextField
            label="Admin Name (optional)"
            value={createAdminName}
            onChange={(e) => setCreateAdminName(e.target.value)}
            disabled={creating}
            helperText="Display name for the admin user (informational)"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !createName.trim() || !createSlug.trim()}
          >
            {creating ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
